// API Configuration
const getApiBaseUrl = () => {
  // Check if running in browser and on production domain
  if (typeof window !== 'undefined' && window.location) {
    if (window.location.hostname === 'talent.adaadev.ae') {
      return 'https://talent-api.adaadev.ae/api/v1';
    }
    if (window.location.hostname === 'talent-stg.adaadev.ae') {
      return 'https://talent-stg-api.adaadev.ae/api/v1';
    }
  }
  // Local dev runs the backend on 8003 (Makefile / start-dev.sh). The Docker
  // stack binds 8013 and injects EXPO_PUBLIC_API_URL, which wins over this.
  return process.env.EXPO_PUBLIC_API_URL || 'http://localhost:8003/api/v1';
};

export const API_BASE_URL = getApiBaseUrl();

// The API origin, used to build photo, thumbnail and download URLs. Derived
// from API_BASE_URL rather than kept as a second hostname list: the parallel
// list had no EXPO_PUBLIC_* escape hatch at all, so on any host it did not
// name — the box's IP, localhost:3040, a new domain — every image and every
// download pointed at http://localhost:8003, and no build arg could fix it.
export const API_BASE_HOST = API_BASE_URL.replace(/\/api\/v\d+\/?$/, '');

// SSO Service Configuration
const getSSOFrontendUrl = () => {
  if (typeof window !== 'undefined' && window.location) {
    if (window.location.hostname === 'sms.adaadev.ae') {
      return 'https://sso.adaadev.ae';
    }
    if (window.location.hostname === 'newsms.adaadev.ae') {
      return 'https://newsso.adaadev.ae';
    }
    if (window.location.hostname === 'talent.adaadev.ae') {
      return 'https://sso.adaadev.ae';
    }
    // Staging talents uses its OWN staging SSO on the Mac
    if (window.location.hostname === 'talent-stg.adaadev.ae') {
      return 'https://sso-stg.adaadev.ae';
    }
  }
  return process.env.EXPO_PUBLIC_SSO_FRONTEND_URL || 'http://localhost:3001';
};

export const SSO_FRONTEND_URL = getSSOFrontendUrl();

// SSO client_id — fetched at runtime from backend so it never needs to be baked in
let _ssoClientId: string = process.env.EXPO_PUBLIC_SSO_CLIENT_ID || 'talent';

export const loadSSOConfig = async (): Promise<void> => {
  try {
    const res = await fetch(`${API_BASE_URL}/auth/config/`);
    if (res.ok) {
      const data = await res.json();
      if (data.client_id) _ssoClientId = data.client_id;
    }
  } catch {
    // Fall back to env/default
  }
};

export const getSSOClientId = () => _ssoClientId;

// Build the SSO login redirect URL
// SSO redirects back to app root with ?code=xxx, AuthContext middleware handles exchange
export const getSSOLoginUrl = () => {
  const redirectUri = encodeURIComponent(`${window.location.origin}/auth/callback`);
  return `${SSO_FRONTEND_URL}/login?client_id=${_ssoClientId}&redirect_uri=${redirectUri}`;
};

// App Configuration
export const APP_NAME = 'نظام إدارة الاستراتيجية';
export const APP_NAME_EN = 'Strategy Management System';

// Fonts - JF Flat font family
// Note: JF Flat only has Regular and Medium weights available
// Bold and SemiBold are aliased to Medium for consistency
export const FONTS = {
  regular: 'JF-Flat-Regular',
  medium: 'JF-Flat-Medium',
  semiBold: 'JF-Flat-Medium',  // Alias to Medium (no SemiBold variant available)
  bold: 'JF-Flat-Medium',      // Alias to Medium (no Bold variant available)
  // Segoe UI for numbers and percentages (system font)
  numbers: '"Segoe UI", system-ui, -apple-system, sans-serif',
};

// Typography System (font sizes only - uses JF Flat font)
export const TYPOGRAPHY = {
  // Headings
  h1: {
    fontSize: 48,
    
    lineHeight: 48, // 100%
  },
  h2: {
    fontSize: 30,
    lineHeight: 30, // 100%
  },
  h3: {
    fontSize: 24,
    lineHeight: 29, // 120%
  },
  h4: {
    fontSize: 20,
    lineHeight: 24, // 120%
  },
  // Paragraphs
  paragraph: {
    fontSize: 16,
    lineHeight: 24, // 150%
  },
  paragraphSmall: {
    fontSize: 14,
    lineHeight: 21, // 150%
  },
  // Mini text
  mini: {
    fontSize: 12,
    lineHeight: 16, // 133%
  },
};

// Theme Colors - Based on SMS Design System
export const COLORS = {
  // Brand Primary
  primary: '#A9211F',
  primaryHover: '#8B1A18',
  primaryLight: '#FEF2F2',

  // Secondary (Blue - Links, Info)
  secondary: '#3B82F6',
  secondaryHover: '#2563EB',
  secondaryLight: '#EFF6FF',

  // Semantic Colors
  success: '#22C55E',
  successHover: '#16A34A',
  successLight: '#DCFCE7',

  warning: '#EAB308',
  warningHover: '#CA8A04',
  warningLight: '#FEF9C3',

  danger: '#EF4444',
  dangerHover: '#DC2626',
  dangerLight: '#FEF2F2',

  info: '#3B82F6',
  infoLight: '#DBEAFE',

  // Neutral Colors
  white: '#FFFFFF',
  black: '#0A0A0A',

  // Neutral Scale (Design System)
  neutral50: '#FAFAFA',
  neutral100: '#F5F5F5',
  neutral200: '#E5E5E5',
  neutral300: '#D4D4D4',
  neutral400: '#A3A3A3',
  neutral500: '#737373',
  neutral600: '#525252',
  neutral700: '#404040',
  neutral800: '#262626',
  neutral900: '#171717',

  // Gray Scale (Tailwind - used across the app)
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',

  // Slate Scale (Tailwind - used for subtle backgrounds)
  slate100: '#F1F5F9',
  slate200: '#E2E8F0',
  slate300: '#CBD5E1',
  slate400: '#94A3B8',
  slate500: '#64748B',
  slate600: '#475569',
  slate700: '#334155',

  // Aliases for convenience
  background: '#FAFAFA',
  backgroundAlt: '#F1F5F9',  // Subtle background
  card: '#FFFFFF',
  cardBorder: '#E5E7EB',     // Card border color
  border: '#E5E5E5',
  borderLight: '#E2E8F0',    // Light border (Tailwind slate-200)
  inputBorder: '#D4D4D4',
  divider: '#F3F4F6',        // Divider lines

  // Text Colors
  text: '#171717',
  textPrimary: '#111827',    // Primary text (Tailwind gray-900)
  textSecondary: '#414042',  // Secondary text (common in app)
  textBody: '#525252',
  textMuted: '#6B7280',      // Muted text (Tailwind gray-500)
  textLight: '#9CA3AF',      // Light text (Tailwind gray-400)
  textDisabled: '#A3A3A3',

  // Icon Colors
  iconDefault: '#414042',    // Default icon color
  iconMuted: '#6B7280',      // Muted icon color
  iconLight: '#9CA3AF',      // Light icon color

  // Legacy aliases (for backward compatibility)
  light: '#F5F5F5',
  dark: '#262626',
  gray: '#737373',
  grayLight: '#E5E5E5',
  grayDark: '#525252',
};

// Status Colors
export const STATUS_COLORS: Record<string, string> = {
  success: COLORS.success,
  warning: COLORS.warning,
  danger: COLORS.danger,
  secondary: COLORS.gray,
};

// Initiative Status Colors
export const INITIATIVE_STATUS_COLORS: Record<string, string> = {
  planning: COLORS.info,
  in_progress: COLORS.primary,
  completed: COLORS.success,
  on_hold: COLORS.warning,
  cancelled: COLORS.danger,
};

// KPI Type Colors
export const KPI_TYPE_COLORS: Record<string, string> = {
  strategic: COLORS.primary,
  operational: COLORS.secondary,
};

// Arabic Labels
export const LABELS = {
  // Navigation
  dashboard: 'لوحة المعلومات',
  organizations: 'الجهات',
  goals: 'الأهداف الاستراتيجية',
  initiatives: 'المبادرات',
  kpis: 'مؤشرات الأداء',
  schedules: 'جدول القراءات',
  notifications: 'الإشعارات',
  settings: 'الإعدادات',
  profile: 'الملف الشخصي',
  logout: 'تسجيل الخروج',
  login: 'تسجيل الدخول',

  // Actions
  add: 'إضافة',
  edit: 'تعديل',
  delete: 'حذف',
  save: 'حفظ',
  cancel: 'إلغاء',
  search: 'بحث',
  filter: 'تصفية',
  refresh: 'تحديث',
  export: 'تصدير',
  import: 'استيراد',

  // Status
  loading: 'جاري التحميل...',
  error: 'حدث خطأ',
  success: 'تم بنجاح',
  noData: 'لا توجد بيانات',

  // KPI
  target: 'المستهدف',
  actual: 'الفعلي',
  achievement: 'نسبة الإنجاز',
  frequency: 'الدورية',
  unit: 'الوحدة',
  owner: 'المالك',

  // Initiative Status
  planning: 'تحت التخطيط',
  in_progress: 'قيد التنفيذ',
  completed: 'مكتملة',
  on_hold: 'معلقة',
  cancelled: 'ملغاة',

  // User Types
  strategy_team: 'فريق الاستراتيجية',
  org_employee: 'موظف جهة',
  executive: 'متخذ القرار',
};
